import { TFunction } from "i18next";

import {
  enumToSelectList,
  IDropdownItem,
  IRichLogRecord,
  RichLogCommaMode,
  RichLogLevel,
  RichLogSorting,
} from "@ryde/shared";

export const richLogSortingOptions = enumToSelectList(RichLogSorting);

export const richLogCommaModeOptions = enumToSelectList(RichLogCommaMode);

export const getUniqueLogStepOptions = (
  records: IRichLogRecord[] | undefined,
): IDropdownItem[] => {
  if (!records?.length) {
    return [];
  }

  const steps = new Set<string>();

  records.forEach(record => {
    const step = record.step?.trim();
    if (step) {
      steps.add(step);
    }
  });

  return [...steps]
    .sort((a, b) => a.localeCompare(b))
    .map(step => ({ value: step, label: step }));
};

const NODE_FILTER_SHOW_TEXT_FALLBACK = 4;
const RICH_LOG_LEVEL_VALUES = Object.values(RichLogLevel);

export const getNormalizedRichLogLevel = (
  level: string | undefined,
): RichLogLevel | undefined => {
  if (!level) {
    return undefined;
  }

  const normalizedLevel = RICH_LOG_LEVEL_VALUES.find(
    value => value.toLowerCase() === level.toLowerCase(),
  );

  return normalizedLevel;
};

export const sortRichLogs = ({
  logs,
  sortType,
}: {
  logs: IRichLogRecord[];
  sortType: RichLogSorting;
}): IRichLogRecord[] => {
  const sortedLogs = [...logs];
  const sortDirection = sortType === RichLogSorting.ByTimeAscending ? 1 : -1;

  sortedLogs.sort((a, b) => {
    if (!a.timestamp && !b.timestamp) {
      return 0;
    }

    if (!a.timestamp) {
      return 1;
    }

    if (!b.timestamp) {
      return -1;
    }

    return sortDirection * a.timestamp.localeCompare(b.timestamp);
  });

  return sortedLogs;
};

export const highlightText = (
  html: string,
  searchText: string | string[],
): string => {
  // Convert to array if it's a string, splitting by commas
  const searchTerms = Array.isArray(searchText)
    ? searchText
    : searchText
        .split(",")
        .map(term => term.trim())
        .filter(term => term.length > 0);

  if (searchTerms.length === 0) {
    return html;
  }

  // Create regex pattern for all search terms
  const escapedSearches = searchTerms.map(term =>
    term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  );
  const regexPattern = `(${escapedSearches.join("|")})`;
  const regex = new RegExp(regexPattern, "gi");

  // Parse HTML and highlight only text content, not attributes
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const walker = doc.createTreeWalker(
    doc.body,
    globalThis.NodeFilter?.SHOW_TEXT ?? NODE_FILTER_SHOW_TEXT_FALLBACK,
  );

  const textNodes: Text[] = [];
  let node: Node | null;

  while ((node = walker.nextNode())) {
    if (node.nodeType === Node.TEXT_NODE) {
      textNodes.push(node as Text);
    }
  }

  textNodes.forEach(textNode => {
    const text = textNode.textContent || "";
    if (!text.match(regex)) {
      return;
    }

    const highlightedText = text.replace(
      regex,
      '<mark class="log-highlight">$1</mark>',
    );
    const wrapper = doc.createElement("span");
    wrapper.innerHTML = highlightedText;

    // Replace the text node with the highlighted content
    const parent = textNode.parentNode;
    if (parent) {
      while (wrapper.firstChild) {
        parent.insertBefore(wrapper.firstChild, textNode);
      }
      parent.removeChild(textNode);
    }
  });

  return doc.body.innerHTML;
};

const recordMatchesSearchTerm = ({
  record,
  term,
  rawTerm,
  showDebugInfo,
  searchInAiHint,
  t,
}: {
  record: IRichLogRecord;
  term: string;
  rawTerm: string;
  showDebugInfo?: boolean;
  searchInAiHint: boolean;
  t: TFunction;
}): boolean => {
  const message = record.message?.toLowerCase() ?? "";
  const translatable = record.translatableItem?.translationKey
    ? t(record.translatableItem.translationKey).toLowerCase()
    : "";

  if (message.includes(term) || translatable.includes(term)) {
    return true;
  }

  if (showDebugInfo) {
    const method = record.method?.toLowerCase() ?? "";
    const errorDetails = record.errorDetails?.toLowerCase() ?? "";
    if (method.includes(term) || errorDetails.includes(term)) {
      return true;
    }
  }

  if (searchInAiHint && record.aiHint) {
    const aiHintLower = record.aiHint.toLowerCase();
    if (aiHintLower.includes(term)) {
      return true;
    }
  }

  const hasLinkMatch = record.messageParams?.linkItems
    ? Object.values(record.messageParams.linkItems).some(
        linkGroup =>
          linkGroup.entity.toLowerCase().includes(term) ||
          linkGroup.linkItems.some(
            item =>
              String(item.id).includes(rawTerm) ||
              String(item.name ?? "").includes(rawTerm),
          ),
      )
    : false;

  if (hasLinkMatch) {
    return true;
  }

  const hasTextInsertionMatch = record.messageParams?.textInsertions
    ? Object.values(record.messageParams.textInsertions).some(insertion =>
        insertion.text?.toLowerCase().includes(term),
      )
    : false;

  return hasTextInsertionMatch;
};

export const filterRecordsExcludingLevels = ({
  records,
  group,
  step,
  searchText,
  commaMode = RichLogCommaMode.Or,
  showDebugInfo,
  withAiHintsOnly = false,
  t,
}: {
  records: IRichLogRecord[] | undefined;
  group: number | null | undefined;
  step: string | undefined;
  searchText: string | undefined;
  commaMode?: RichLogCommaMode;
  showDebugInfo?: boolean;
  withAiHintsOnly?: boolean;
  t: TFunction;
}): IRichLogRecord[] => {
  if (!records?.length) {
    return [];
  }

  const normalizedSearchTerms = searchText
    ?.split(",")
    .map(term => term.trim().toLowerCase())
    .filter(Boolean);
  const rawSearchTerms = searchText
    ?.split(",")
    .map(term => term.trim())
    .filter(Boolean);

  let result = [...records];

  if (group !== undefined && group !== null) {
    result = result.filter(
      r => r.group !== undefined && String(r.group) === String(group),
    );
  }
  if (step) {
    result = result.filter(r => r.step === step);
  }

  if (withAiHintsOnly) {
    result = result.filter(r => Boolean(r.aiHint?.trim()));
  }

  if (!normalizedSearchTerms?.length) {
    return result;
  }

  const searchInAiHint = Boolean(showDebugInfo);
  const hasMultipleCommaTerms = normalizedSearchTerms.length > 1;

  if (commaMode === RichLogCommaMode.And && hasMultipleCommaTerms) {
    result = result.filter(record =>
      normalizedSearchTerms.every((term, index) =>
        recordMatchesSearchTerm({
          record,
          term,
          rawTerm: rawSearchTerms?.[index] ?? term,
          showDebugInfo,
          searchInAiHint,
          t,
        }),
      ),
    );
  } else {
    result = result.filter(record =>
      normalizedSearchTerms.some((term, index) =>
        recordMatchesSearchTerm({
          record,
          term,
          rawTerm: rawSearchTerms?.[index] ?? term,
          showDebugInfo,
          searchInAiHint,
          t,
        }),
      ),
    );
  }

  return result;
};
